import { For, Show } from "solid-js";
import { Text, View, hexColor } from "@lightningtv/solid";
import { changeLanguage, locale, t } from "../../../translations/configuration";

type LanguagePickerProps = {
  // Called when Left is pressed on the first tile, to leave the picker
  // (e.g. back to the "Language" item in the left menu).
  onLeftEdge: () => void;
  // Hands the parent a ref to the first tile, so it can send focus straight
  // into the picker (e.g. the menu item's onRight).
  firstTileRef?: (el: any) => void;
};

const LanguagePicker = (props: LanguagePickerProps) => {
  let langTileRefs: any[] = [];

  // Built inside the component (not at module scope) so the labels use
  // whatever locale is active each time this screen is opened — this panel
  // is unmounted/remounted by the <Show> that guards it in SettingsPanel,
  // so that's every time the user enters the Language section, not just
  // once at app startup.
  const LANGUAGES = [
    { code: "en", label: t("english"), flag: "https://i.ibb.co/7x245bF6/usa.png" },
    { code: "it", label: t("italian"), flag: "https://i.ibb.co/VYD4hq0t/Flag-of-Italy-1946-2003.png" },
    { code: "de", label: t("german"), flag: "https://i.ibb.co/27mzrq4t/Flag-of-Germany-svg.webp" },
    { code: "es", label: t("spanish"), flag: "https://i.ibb.co/0Nds6Fx/Spain-flag-300.png" },
    { code: "fr", label: t("french"), flag: "https://i.ibb.co/Fb5JmryQ/Flag-of-France.png" },
  ];

  return (
    <View x={550} y={50} height={900} width={1200} clipping>
      <Text style={{ fontSize: 40, fontWeight: "bold", color: hexColor("#ffffff") }}>{t("chooseLang")}</Text>

      <View
        y={100}
        style={{ display: "flex", flexDirection: "row", flexWrap: "wrap", gap: 20, width: 1200 }}
      >
        <For each={LANGUAGES}>
          {(lang, index) => {
            const isActive = () => locale() === lang.code;

            return (
              <View
                ref={(el: any) => {
                  langTileRefs[index()] = el;
                  if (index() === 0) props.firstTileRef?.(el);
                }}
                onEnter={() => changeLanguage(lang.code)}
                onLeft={() => {
                  const prev = langTileRefs[index() - 1];
                  if (prev) prev.setFocus();
                  else props.onLeftEdge();
                }}
                onRight={() => {
                  const next = langTileRefs[index() + 1];
                  if (next) next.setFocus();
                }}
                style={{
                  // Round tile — same width/height makes borderRadius: half
                  // draw a circle. Focus/active logic below is unchanged.
                  borderRadius: 70,
                  width: 148,
                  height: 148,
                  scale: 1,
                  zIndex: 2,
                  transition: { scale: { duration: 250, easing: "ease-in-out" } },
                  focus: { color: hexColor("#ffffff"), scale: 1.02 },
                  color: hexColor("#484e55"),
                  alignItems: "center",
                }}
              >
                {/* Flag is masked into its own clipped node — clipping and
                    the tile's border/focus fill living on the same node
                    would clip the border/fill away too. */}
                <View
                  x={4}
                  y={4}
                  width={140}
                  height={140}
                  src={lang.flag}
                  style={{ objectFit: "cover", borderRadius: 70 }}
                />

                <Text
                  skipFocus
                  y={150}
                  textAlign="center"
                  contain="width"
                  style={{ fontSize: 18, color: hexColor("#ffffff"), width: 140 }}
                >
                  {lang.label}
                </Text>

                {/* Small badge on the active language's flag corner */}
                <Show when={isActive()}>
                  <View
                    skipFocus
                    x={104}
                    y={-4}
                    width={26}
                    height={26}
                    style={{ borderRadius: 13, color: hexColor("#4CD964") }}
                  >
                    <Text
                      skipFocus
                      x={6}
                      y={3}
                      style={{ fontSize: 16, fontWeight: "bold", color: hexColor("#000000") }}
                    >
                      ✓
                    </Text>
                  </View>
                </Show>
              </View>
            );
          }}
        </For>
      </View>
    </View>
  );
};

export default LanguagePicker;
